import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';

// Create context
const CartContext = createContext();

// Custom hook to use the cart context
export const useCart = () => {
  return useContext(CartContext);
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [merchant, setMerchant] = useState(null);
  const [table, setTable] = useState(null);

  // Load cart from localStorage on initial render
  useEffect(() => {
    const storedCart = localStorage.getItem('cart');
    const storedMerchant = localStorage.getItem('merchant');
    const storedTable = localStorage.getItem('table');

    if (storedCart) {
      try {
        const parsedCart = JSON.parse(storedCart);
        setItems(parsedCart);
      } catch (error) {
        console.error('Error parsing cart from localStorage:', error);
        setItems([]);
      }
    }

    if (storedMerchant) {
      try {
        const parsedMerchant = JSON.parse(storedMerchant);
        setMerchant(parsedMerchant);
      } catch (error) {
        console.error('Error parsing merchant from localStorage:', error);
        setMerchant(null);
      }
    }

    if (storedTable) {
      try {
        const parsedTable = JSON.parse(storedTable);
        setTable(parsedTable);
      } catch (error) {
        console.error('Error parsing table from localStorage:', error);
        setTable(null);
      }
    }
  }, []);

  // Update localStorage when cart changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));

    // Calculate total items and price
    let itemCount = 0;
    let price = 0;

    items.forEach(item => {
      itemCount += item.quantity;

      // Calculate item price including options
      let itemPrice = parseFloat(item.price) || 0;
      if (item.options && item.options.length > 0) {
        item.options.forEach(option => {
          const optionPrice = parseFloat(option.price_addition) || 0;
          itemPrice += optionPrice;
        });
      }

      price += itemPrice * item.quantity;
    });

    setTotalItems(itemCount);
    setTotalPrice(price);
  }, [items]);

  // Update localStorage when merchant changes
  useEffect(() => {
    if (merchant) {
      localStorage.setItem('merchant', JSON.stringify(merchant));
    } else {
      localStorage.removeItem('merchant');
    }
  }, [merchant]);

  // Update localStorage when table changes
  useEffect(() => {
    if (table) {
      localStorage.setItem('table', JSON.stringify(table));
    } else {
      localStorage.removeItem('table');
    }
  }, [table]);

  const addItem = useCallback((item, quantity = 1, options = []) => {
    setItems(prevItems => {
      const existingItemIndex = prevItems.findIndex(
        cartItem =>
          cartItem.id === item.id &&
          JSON.stringify(cartItem.options) === JSON.stringify(options)
      );

      if (existingItemIndex !== -1) {
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].quantity += quantity;
        return updatedItems;
      } else {
        return [...prevItems, { ...item, quantity, options }];
      }
    });
  }, []);

  const updateItemQuantity = useCallback((itemIndex, quantity) => {
    setItems(prevItems => {
      const updatedItems = [...prevItems];

      if (quantity <= 0) {
        updatedItems.splice(itemIndex, 1);
      } else {
        updatedItems[itemIndex].quantity = quantity;
      }

      return updatedItems;
    });
  }, []);

  const updateItem = useCallback((itemIndex, updatedItem) => {
    setItems(prevItems => {
      if (itemIndex < 0 || itemIndex >= prevItems.length) return prevItems;
      const updatedItems = [...prevItems];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        ...updatedItem,
      };
      return updatedItems;
    });
  }, []);

  const removeItem = useCallback((itemIndex) => {
    setItems(prevItems => {
      const updatedItems = [...prevItems];
      updatedItems.splice(itemIndex, 1);
      return updatedItems;
    });
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const getItemQuantity = useCallback((itemId, options = []) => {
    const cartItem = items.find(
      item =>
        item.id === itemId &&
        JSON.stringify(item.options) === JSON.stringify(options)
    );
    return cartItem ? cartItem.quantity : 0;
  }, [items]);

  const setMerchantAndTable = useCallback((newMerchant, newTable) => {
    setMerchant(prevMerchant => {
      if (prevMerchant && newMerchant && prevMerchant.id !== newMerchant.id) {
        setItems([]);
      }
      return newMerchant;
    });
    setTable(prevTable => {
      if (prevTable && newTable && prevTable.id !== newTable.id) {
        setItems([]);
      }
      return newTable;
    });
  }, []);

  const orderBreakdown = useMemo(() => {
    const subtotal = totalPrice;
    const settings = merchant?.settings || {};
    const taxPct = parseFloat(settings.tax_percentage) || 0;
    const serviceChargePct = parseFloat(settings.service_charge_percentage) || 0;
    const autoDiscounts = settings.auto_discounts || [];

    let totalDiscount = 0;
    const discountDetails = autoDiscounts.map((d) => {
      const val = Math.max(parseFloat(d.value) || 0, 0);
      let amount = 0;
      if (d.discount_type === 'percentage') {
        amount = subtotal * (val / 100);
      } else {
        amount = val;
      }
      amount = Math.min(amount, subtotal);
      totalDiscount += amount;
      return { ...d, amount };
    });

    totalDiscount = Math.min(totalDiscount, subtotal);
    const afterDiscount = Math.max(subtotal - totalDiscount, 0);
    const tax = afterDiscount * (taxPct / 100);
    const serviceCharge = afterDiscount * (serviceChargePct / 100);
    const platformFee = parseFloat(settings.platform_service_fee) || 0;
    const total = afterDiscount + tax + serviceCharge + platformFee;

    return {
      subtotal,
      taxPercentage: taxPct,
      serviceChargePercentage: serviceChargePct,
      tax,
      serviceCharge,
      platformFee,
      discountDetails,
      totalDiscount,
      total,
    };
  }, [totalPrice, merchant]);

  const value = {
    items,
    totalItems,
    totalPrice,
    merchant,
    table,
    orderBreakdown,
    addItem,
    updateItem,
    updateItemQuantity,
    removeItem,
    clearCart,
    getItemQuantity,
    setMerchantAndTable
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
